import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BusinessErrorDialog } from '@/components/ui/business-error-dialog';
import { setBusinessErrorHandler, clearBusinessErrorHandler } from '@/utils/businessErrorHandler';
import { translateErrorCode, isErrorCode } from '@/utils/errorUtils';

interface ErrorDialogContextType {
  showBusinessError: (message: string) => void;
}

const ErrorDialogContext = createContext<ErrorDialogContextType | undefined>(undefined);

export const useErrorDialog = () => {
  const context = useContext(ErrorDialogContext);
  if (!context) {
    throw new Error('useErrorDialog must be used within ErrorDialogProvider');
  }
  return context;
};

interface ErrorDialogProviderProps {
  children: React.ReactNode;
}

export const ErrorDialogProvider: React.FC<ErrorDialogProviderProps> = ({ children }) => {
  const { t } = useTranslation('wellness');
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const showBusinessError = useCallback((message: string) => {
    // Prevent multiple dialogs from showing simultaneously
    if (isOpen) {
      return;
    }
    
    // Translate error code if it's an error code, otherwise use as-is
    const translatedMessage = isErrorCode(message)
      ? translateErrorCode(message, t)
      : message;
    
    setErrorMessage(translatedMessage);
    setIsOpen(true);
  }, [isOpen, t]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Clear message after a short delay to allow animation to complete
    setTimeout(() => {
      setErrorMessage('');
    }, 200);
  }, []);

  // Register the handler function with the business error handler utility
  useEffect(() => {
    setBusinessErrorHandler(showBusinessError);
    return () => {
      clearBusinessErrorHandler();
    };
  }, [showBusinessError]);

  return (
    <ErrorDialogContext.Provider value={{ showBusinessError }}>
      {children}
      <BusinessErrorDialog
        open={isOpen}
        onOpenChange={handleClose}
        message={errorMessage}
      />
    </ErrorDialogContext.Provider>
  );
};

